import { useTranslation } from "react-i18next";
import React, { useMemo, useRef, useState } from "react";
import { Form, FormInstance, Table, TableProps } from "antd";
import { useGetAllReviewsWithPaginationQuery } from "../../api/queries";
import dayjs from "dayjs";
import { IReview } from "../../models/Review";
import { ColumnsType } from "antd/es/table";
import dots from "../../assets/images/icons/dots.svg";
import viewIcon from "../../assets/images/icons/eye-dark.svg";
import deleteIcon from "../../assets/images/icons/basket-black.svg";
import reportIcon from "../../assets/images/icons/flag-dark.svg";
import MainTitle from "../../components/ui/MainTitle/MainTitle";
import FormItemDatePicker from "../../components/ui/FormItemDatePicker/FormItemDatePicker";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import searchIconWhite from "../../assets/images/icons/search-icon-white.svg";
import ExpandedText from "../../components/ExpandedText/ExpandedText";
import { useReviewMutations } from "../../api/mutations";
import ConfirmPopup from "../../components/ConfirmPopup/ConfirmPopup";
import { handleError } from "../../helper/errorHandler";
import { openSuccessMessage } from "../../store/message/messageSlice";
import useAppDispatch from "../../hooks/useAppDispatch";
import ReviewDetails from "./ReviewDetails/ReviewDetails";
import redFlagIcon from "../../assets/images/icons/flag-red.svg";

const Reviews = () => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  const dispatch = useAppDispatch();

  const [filters, setFilters] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  });
  const [tableParams, setTableParams] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const [selectedForView, setSelectedForView] = useState<IReview | null>(null);
  const [selectedForDelete, setSelectedForDelete] = useState<IReview | null>(null);
  const [selectedForReport, setSelectedForReport] = useState<IReview | null>(null);

  const { reportReview, deleteReview } = useReviewMutations();
  const {
    data = { data: [], totalCount: 0 },
    isLoading,
    refetch,
  } = useGetAllReviewsWithPaginationQuery({
    currentPage: tableParams.current,
    pageSize: tableParams.pageSize,
    queryOptions: filters,
  });
  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.totalCount;
  }, [data]);

  const handleTableChange: TableProps<IReview>["onChange"] = (pagination) => {
    setTableParams({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const deleteReviewFn = async () => {
    if (!selectedForDelete) return;
    deleteReview(selectedForDelete.id, {
      onSuccess: () => onSuccessFn("ReviewDeletedSuccessfully"),
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const reportReviewFn = async () => {
    if (!selectedForReport) return;
    reportReview(selectedForReport.id, {
      onSuccess: () =>
        onSuccessFn(
          selectedForReport.isReported
            ? "ReviewUnReportedSuccessfully"
            : "ReviewReportedSuccessfully"
        ),
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const onFinish = (values: { from: Date | undefined; to: Date | undefined }) => {
    const model = {
      from: values.from ? values.from.toISOString() : null,
      to: values.to ? values.to.toISOString() : null,
    };
    setFilters(model);
  };

  const onSuccessFn = async (message: string) => {
    onClickNo();
    refetch();
    if (formRef.current) {
      formRef.current.setFieldsValue({
        from: null,
        to: null,
      });
    }
    dispatch(openSuccessMessage(t(message)));
  };

  const onClickNo = () => {
    setSelectedForView(null);
    setSelectedForDelete(null);
    setSelectedForReport(null);
  };

  const columns: ColumnsType<IReview> = [
    {
      title: t("User"),
      render: (data: IReview) => {
        return `${data.user.firstName} ${data.user.lastName}`;
      },
    },
    {
      title: t("GymName"),
      render: (data: IReview) => {
        return data.gymListing.businessName;
      },
    },
    {
      title: t("Date"),
      render: (data: IReview) => {
        return `${dayjs(data.createdAt).format("DD/MM/YYYY")}`;
      },
    },
    {
      title: t("Rating"),
      render: (data: IReview) => {
        return (
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <g clip-path="url(#clip0_24037_20095)">
                <path
                  d="M7.2795 1.91199C7.35287 1.78609 7.45796 1.68164 7.58429 1.60904C7.71063 1.53644 7.85379 1.49823 7.9995 1.49823C8.14521 1.49823 8.28838 1.53644 8.41471 1.60904C8.54105 1.68164 8.64614 1.78609 8.7195 1.91199L10.5828 5.11065L14.2015 5.89465C14.3438 5.92558 14.4756 5.99329 14.5836 6.09101C14.6916 6.18873 14.7721 6.31306 14.8171 6.4516C14.8621 6.59014 14.87 6.73806 14.84 6.8806C14.81 7.02314 14.7432 7.15533 14.6462 7.26399L12.1795 10.0247L12.5528 13.708C12.5676 13.853 12.544 13.9994 12.4845 14.1325C12.4249 14.2655 12.3315 14.3807 12.2136 14.4664C12.0956 14.552 11.9573 14.6053 11.8123 14.6208C11.6674 14.6363 11.5209 14.6135 11.3875 14.5547L7.9995 13.0613L4.6115 14.5547C4.47811 14.6135 4.33163 14.6363 4.18667 14.6208C4.04171 14.6053 3.90336 14.552 3.78542 14.4664C3.66748 14.3807 3.57408 14.2655 3.51455 14.1325C3.45502 13.9994 3.43144 13.853 3.44617 13.708L3.8195 10.0247L1.35284 7.26465C1.25565 7.156 1.18867 7.02376 1.15858 6.88113C1.12848 6.7385 1.13633 6.59047 1.18133 6.45182C1.22633 6.31317 1.30692 6.18875 1.41504 6.09099C1.52316 5.99322 1.65504 5.92552 1.7975 5.89465L5.41617 5.11065L7.2795 1.91199Z"
                  fill="#FF8C42"
                />
              </g>
              <defs>
                <clipPath id="clip0_24037_20095">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
            {data.rating}
          </div>
        );
      },
    },
    {
      title: t("IsReported"),
      render: (data: IReview) => {
        if (data.isReported) {
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              <img src={redFlagIcon} alt={"flag"} />
              {t("Yes")}
            </div>
          );
        }
        return t("No");
      },
    },
    {
      title: t("Comment"),
      render: (data: IReview) => {
        return (
          <div style={{ maxWidth: "430px" }}>
            <ExpandedText text={data.comment} maxLength={40} />
          </div>
        );
      },
    },
    {
      title: t("Action"),
      render: (data: IReview) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li className={"dropdown-item"} onClick={() => setSelectedForView(data)}>
                <img src={viewIcon} alt={"view"} />
                {t("View")}
              </li>
              <li className={"dropdown-item"} onClick={() => setSelectedForDelete(data)}>
                <img src={deleteIcon} alt={"delete"} />
                {t("Delete")}
              </li>
              <li className={"dropdown-item"} onClick={() => setSelectedForReport(data)}>
                <img src={reportIcon} alt={"report"} />
                {!data.isReported ? t("Report") : t("UnReport")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className={"page-content gyms-page"}>
        <MainTitle text={t("Reviews")} />

        <div className={"top-row"}>
          <Form
            className={"form"}
            ref={formRef}
            name="gym-listing-filter"
            onFinish={onFinish}
            autoComplete="off"
            layout={"vertical"}
          >
            <FormItemDatePicker
              dateFormat={"DD/MM/YYYY"}
              name={"from"}
              label={t("From")}
              rules={{ required: false }}
              style={{
                width: "30%",
              }}
            />
            <FormItemDatePicker
              dateFormat={"DD/MM/YYYY"}
              name={"to"}
              label={t("To")}
              rules={{ required: false }}
              style={{
                width: "30%",
              }}
            />
            <div className={"btn-wrapper"}>
              <CustomButton
                text={""}
                type={"submit"}
                typeStyle={"fill"}
                icon={searchIconWhite}
                altIcon={"search"}
              />
            </div>
          </Form>
        </div>

        <Table
          className={"table"}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data.data}
          pagination={{ ...tableParams, total: totalCount }}
          loading={isLoading}
          onChange={handleTableChange}
        />
      </div>
      {selectedForView && <ReviewDetails review={selectedForView} closePopup={onClickNo} />}
      {selectedForReport && (
        <ConfirmPopup
          title={
            selectedForReport.isReported
              ? t("AreYouSureYouWantToUnReportThisComment")
              : t("AreYouSureYouWantToReportThisComment")
          }
          onClickYes={reportReviewFn}
          onClickNo={onClickNo}
        />
      )}
      {selectedForDelete && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToDeleteThisComment")}
          onClickYes={deleteReviewFn}
          onClickNo={onClickNo}
        />
      )}
    </>
  );
};

export default Reviews;
